import type { Permission } from "@mainsail/schema";
import type { AuthServiceActiveUserFragment } from "../../gql/graphql";
import type { DeepReadonly } from "../../util/type";
import type { ApiService } from "../api/api-service";
import type { BrowserService } from "../browser/browser-service";
import type { NotificationService } from "../notification/notification-service";
import type { GangplankClient } from "./gangplank-client";
import type { PermissionQuery } from "./permission-query";
import { bound, notify, observable } from "ecce-react";
import deepEqual from "deep-equal";
import { MasqueradeIcon } from "../../components/atoms/icons/masquerade-icon";
import { MasqueradeController } from "./masquerade-controller";


export type ActiveUser = DeepReadonly<AuthServiceActiveUserFragment["activeUser"]>;

export type AuthServiceConfig = {
	activeUser: ActiveUser;
};

export type AuthServiceDependencies = {
	browser: BrowserService;
	notification: NotificationService;
	api: ApiService;
	gangplank: GangplankClient;
};

export type MasqueradeEvent = {
	status: "begin" | "end" | "failed";
	user: ActiveUser;
};
export type MasqueradeListener = (
	| ((ev: MasqueradeEvent) => void)
	| ((ev: MasqueradeEvent) => Promise<void>)
);

class PermissionError extends Error {
	constructor(permission: Permission, message?: string) {
		super(`Expected ${permission}${message ? ": " + message : ""}`);
		this.name = "PermissionError";
	}
}

export class AuthService {
	static readonly MASQUERADE_COOKIE = "ci_masquerade";
	static readonly MASQUERADE_STATE_KEY = "masqueradeState";
	static readonly MASQUERADE_STATE_STARTED = "started";
	static readonly MASQUERADE_STATE_ENDED = "ended";
	static readonly MASQUERADE_STATE_FAILED = "failed";

	readonly #browser: BrowserService;
	readonly #gangplank: GangplankClient;
	readonly #api: ApiService;
	readonly #notification: NotificationService;

	#activeUser: ActiveUser | null = null;
	#permissions: ReadonlySet<Permission> | null = null;

	#masqueradeController: MasqueradeController | null = null;
	readonly #masqueradeListeners = new Set<MasqueradeListener>();

	constructor(deps: Readonly<AuthServiceDependencies>) {
		this.#browser = deps.browser;
		this.#gangplank = deps.gangplank;
		this.#api = deps.api;
		this.#notification = deps.notification;

		this.#api.on("unauthorized", async () => {
			// Handle failed masquerade.
			if(this.masquerading) {
				setTimeout(async () => {
					await this.#notifyMasqueradeListeners("failed");
					this.#browser.setLocalStorage(AuthService.MASQUERADE_STATE_KEY, AuthService.MASQUERADE_STATE_FAILED);
					this.#browser.deleteCookie(AuthService.MASQUERADE_COOKIE);
					this.#browser.refresh();
					return;
				}, 100);
			}

			this.#gangplank.signIn();
		});
	}

	/**
	 * Called by `Services`.
	 */
	private initialise(activeUser: ActiveUser) {
		this.#setActiveUser(activeUser);

		setTimeout(() => {
			switch(this.#browser.getLocalStorage(AuthService.MASQUERADE_STATE_KEY)) {
				case AuthService.MASQUERADE_STATE_STARTED: {
					this.#notification.send({
						severity: "success",
						icon: MasqueradeIcon,
						titleKey: "admin:masquerade.masqueradingAs",
						titleValues: { name: activeUser.name },
					});
					break;
				}
				case AuthService.MASQUERADE_STATE_FAILED: {
					this.#notification.send({
						icon: MasqueradeIcon,
						severity: "error",
						titleKey: "admin:masquerade.failedToMasquerade",
					});
					break;
				}
				case AuthService.MASQUERADE_STATE_ENDED: {
					this.#notification.send({
						icon: MasqueradeIcon,
						severity: "info",
						titleKey: "admin:masquerade.noLongerMasquerading",
					});
					break;
				}
			}

			this.#browser.deleteLocalStorage(AuthService.MASQUERADE_STATE_KEY);
		}, 250);
	}

	#setActiveUser(activeUser: ActiveUser) {
		if(deepEqual(this.#activeUser, activeUser)) {
			return;
		}

		this.#activeUser = activeUser;
		this.#permissions = new Set(activeUser.permissions);

		notify(this, "activeUser");
	}

	async #notifyMasqueradeListeners(status: MasqueradeEvent["status"]) {
		const event = { status, user: this.activeUser };
		await Promise.allSettled([ ...this.#masqueradeListeners ].map(listener => (
			listener(event)
		)));

		notify(this, "masquerading");
	}

	@observable()
	get activeUser(): ActiveUser {
		if(!this.#activeUser) {
			throw new Error("AuthService was not ready.");
		}

		return this.#activeUser;
	}

	@bound()
	hasPermission(permission: Permission): boolean {
		return this.#permissions?.has(permission) ?? false;
	}

	assertPermission(permission: Permission, message?: string): void {
		if(!this.hasPermission(permission)) {
			throw new PermissionError(permission, message);
		}
	}

	/**
	 * Check if the active user has all of the specified permissions.
	 */
	hasAllPermissions(permissions: readonly Permission[]): boolean {
		if(!permissions.length) {
			throw new RangeError("Permissions array must not be empty.");
		}

		for(const permission of permissions) {
			if(!this.hasPermission(permission)) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Check if the active user has at least one of the specified permissions.
	 */
	hasSomePermissions(permissions: readonly Permission[]): boolean {
		if(!permissions.length) {
			throw new RangeError("Permissions array must not be empty.");
		}

		for(const permission of permissions) {
			if(this.hasPermission(permission)) {
				return true;
			}
		}

		return false;
	}

	permissionQuery(query: DeepReadonly<PermissionQuery>): boolean {
		if(typeof(query) === "string") {
			return this.hasPermission(query);
		}

		if("all" in query && !("some" in query)) {
			const all = this.hasAllPermissions(query.all);
			if(all && query.and) {
					return this.permissionQuery(query.and);
			}
			return all;
		}

		if("some" in query && !("all" in query)) {
			const some = this.hasSomePermissions(query.some);
			if(some && query.and) {
				return this.permissionQuery(query.and);
			}
			return some;
		}

		throw new Error("Invalid PermissionQuery:" + JSON.stringify(query));
	}

	@observable()
	get masquerading(): boolean {
		return this.#browser.hasCookie(AuthService.MASQUERADE_COOKIE);
	}

	async beginMasquerade(userId: string): Promise<void> {
		await this.#notifyMasqueradeListeners("begin");
		this.#browser.setLocalStorage(AuthService.MASQUERADE_STATE_KEY, AuthService.MASQUERADE_STATE_STARTED);

			this.#browser.setCookie(AuthService.MASQUERADE_COOKIE, userId);
			this.#browser.refresh();
	}

	@bound()
	async endMasquerade(): Promise<void> {
		if(!this.masquerading) {
			return;
		}

		await this.#notifyMasqueradeListeners("end");
		this.#browser.setLocalStorage(AuthService.MASQUERADE_STATE_KEY, AuthService.MASQUERADE_STATE_ENDED);

			this.#browser.deleteCookie(AuthService.MASQUERADE_COOKIE);
			this.#browser.refresh();
	}

	@bound()
	signOut(): void {
		this.#gangplank.signOut();
	}

	on(event: "masquerade", listener: MasqueradeListener): void {
		this.#masqueradeListeners.add(listener);
	}

	off(event: "masquerade", listener: MasqueradeListener): void {
		this.#masqueradeListeners.delete(listener);
	}

	get masqueradeController(): MasqueradeController {
		return this.#masqueradeController ??= new MasqueradeController(this.#api, this);
	}
}
